/* eslint-disable max-lines */
import {
  Component, OnInit, OnDestroy, Inject, Input, SimpleChanges, OnChanges, Output, EventEmitter
} from '@angular/core';
import { ViewportScroller, DatePipe } from '@angular/common';
import { Subscription, Observable, combineLatest, iif, zip, of } from 'rxjs';
import { Router, ActivatedRoute, Params, NavigationEnd } from '@angular/router';
import isEmpty from 'lodash-es/isEmpty';
import { TranslocoService } from '@jsverse/transloco';
import { filter, first, map, switchMap, take, tap } from 'rxjs/operators';

import { isNullOrUndefined } from '@app/util/util';
import { PassengerArrangement, PassengerType } from '@shared/base-search-bar/entities/passenger-arrangement';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { FlightType } from '@search-bar/entities/flight-type';
import { FlightClass } from '@shared/base-search-bar/entities/flight-class';
import { flexiDatesRoute, flightResultsRootRoute } from '@flight-results/flight-results-routing.constants';
import { mapSearchData, mapUrlFlexDatesParams } from '@search-bar/mappers/url-search.mapper';
import { SearchType } from '@search-bar/entities/search-type';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { Airport } from '@app/modules/core/interfaces/airport';
import { AutocompletePlaces } from '@search-bar/entities/autocomplete-places';
import { ChangeInputPayload } from '@shared/base-search-bar/entities/search-input-payload';
import { AutocompletePlace } from '@shared/base-search-bar/entities/autocomplete';
import { SelectAutocompletePlace } from '@search-bar/entities/select-autocomplete-place';
import { AppFacade } from '@app/app.facade';
import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { CalendarField } from '@shared/base-search-bar/entities/calendar.entities';
import { busResultsRootRoute } from '@buses/bus-results/bus-results-routing.module';
import { PlaceType } from '@shared/base-search-bar/entities/place-type';
import {
  blockSearchByDates, isNationalSearch, canSearch, blockSearchByCountry, untilDateByCountry, formatDate
} from '@ux/mappers/date.mapper';
import { ToastrTypes } from '@app/entities/toastr';
import { RappiTravelModules } from '@ux/entities/modules';
import { BusType } from '@search-bar/entities/bus-type';
import { SearchDataDefaultType } from '@search-bar/entities/search-data-default';
import { TranslatePipe } from '@app/translate/TranslatePipe';
import { SearchData } from '@shared/base-search-bar/entities/search-data';
import { ExchangeSearchParamsSectors } from '@user-history/entities/user-history-item';
import { exchangeTripTypeMapper, mapExchangeSearchData } from '@search-bar/mappers/exchange-url.mapper';
import { flightExchangeRootRoute } from '@flight-results/flight-exchanges-routing.module';
import { homeItemDetailRootRoute } from '@app/modules/home-item-detail/home-item-detail-routing.module';
import { TripTypes } from '@app/entities/trip-types';
import { SALES_CHANNEL } from '@app/modules/core/constants/query-params';
import { AmplitudeEventParams } from '@amplitude/interfaces/amplitude-events';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { triggerAmplitudeEventApp } from '@amplitude/helpers/gtm-functions';
import {
  getExchangeWithAssistanceAndLongerDuration, getExchangeWithAssistanceAndShorterOrEqualDuration
} from '@user-history/mappers/exchange-with-assistance.mapper';
import { SwipeType } from '@ux/entities/swipe-types.enum';
import { AvoService } from '@avo/services/avo.service';
import { AvoDispatchOrchestrator } from '@avo/orchestrator/avo.dispatch';
import { AVO_EVENTS } from '@avo/events/avo.events';
import { mapFlexDatesPayload } from '@flight-results/mappers/search-payload.mapper';
import { getBaseRequestSettings } from '@app/mappers/reguest-settings.mapper';
import { FlightResultsFacade } from '@flight-results/flight-results.facade';
import { SearchProperties } from '@app/modules/avo/interface/trigger-props';
import { TriggerEvent } from '@app/modules/avo/interface/trigger-entity';
import { SelectFlexdatesProperties, SelectTravelSearchProperties } from '@app/modules/avo/Avo';
import { RefreshSearchFacade } from '@shared/refresh-search/store/refresh-search.facade';
import { HeaderProductType } from '@header/entities/ds-header-config';


@Component({
  selector: 'app-search-bar',
  templateUrl: './search-bar.container.html',
  styleUrls: ['./search-bar.container.sass'],
  standalone: false
})
export class SearchBarContainer implements OnInit, OnDestroy, OnChanges {

  @Input() searchType: SearchType;
  @Input() isMobile: boolean;
  @Input() airports: Airport[];
  @Input() loadSearchData: boolean = false;
  @Input() isExchangeSearch: boolean = false;
  @Input() source: EVENT_SOURCE | string = EVENT_SOURCE.TAG;
  @Input() showHeaderMobile: boolean = true;
  @Input() set exchangeData(exchangeData: ExchangeSearchParamsSectors) {
    this._exchangeData = exchangeData;
  }
  @Input() set airlineCode(airlineCode: string) {
    this._airlineCode = airlineCode; if (airlineCode) this.facade.selectAirline(airlineCode);
  }
  @Input() destination: boolean;
  @Input() isLandingDestination: boolean;
  @Input() validate: boolean;
  @Input() loadDefaultplaces: boolean;
  @Input() enabledEditControl = true;

  @Output() showMobileCalendarEvent: EventEmitter<CalendarField> = new EventEmitter<CalendarField>();

  public currentUrl: string | null = null;
  public authToken: string;
  public defaultPlace: SelectAutocompletePlace;
  public swipeStyle: SwipeType = SwipeType.FULL;
  public isCheckedFlexDate = false;

  private subscriptions: Subscription[] = [];
  private _airlineCode: string;
  private _exchangeData: ExchangeSearchParamsSectors;

  protected AvoDispatch = new AvoDispatchOrchestrator();

  constructor(
    private readonly appFacade: AppFacade,
    private readonly facade: BaseSearchBarFacade,
    private readonly viewportScroller: ViewportScroller,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly translateService: TranslocoService,
    private readonly datePipe: DatePipe,
    private readonly translatePipe: TranslatePipe,
    private readonly amplitude: PropertiesAmplitudeService,
    private readonly avoService: AvoService,
    private readonly flightResultFacade: FlightResultsFacade,
    private readonly refreshSearchFacade: RefreshSearchFacade,
    @Inject(IS_BROWSER) public isBrowser: boolean,
  ) {
    this.loadDefaultplaces = true;
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      if (!this.exchangeSearch && !this.isLandingDestination && this.loadDefaultplaces) {
        this.setDefaultPlace(); this.fetchDefaultPlace();
      }
      if (!this.editSearchResults && this.loadDefaultplaces) {
        this.facade.resetAlertsData();
        this.facade.loadSearchData((this.isBuses ? SearchBy.BUSES : SearchBy.FLIGHTS));
      }
      this.currentUrl = this.router.url;
      this.subscriptions.push(this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe(u => this.currentUrl = u.url));
      if (!this.isResultPage) this.registerViewSearchAmplitudeEvent();
      this.subscriptions.push(this.registerViewTravelSearchScreenByEdit());
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['loadSearchData']?.previousValue && this.editSearchResults && !this.exchangeSearch) {
      this.facade.resetAlertsData();
      this.facade.loadSearchData((this.isBuses ? SearchBy.BUSES : SearchBy.FLIGHTS));
    }
  }

  private get headerProductType(): HeaderProductType {
    return this.exchangeSearch ? HeaderProductType.Exchanges : HeaderProductType.Air;
  }

  get editSearchIsActivated$(): Observable<boolean> {
    return this.facade.editSearchIsActivated$(this.headerProductType);
  }

  get searchData$(): Observable<SearchData> {
    return this.facade.searchData$;
  }
  get isEditButtonVisible$(): Observable<boolean> {
    return this.facade.isHeaderEditButtonVisible$.pipe(
      map(isHeaderEditButtonVisible => isHeaderEditButtonVisible && this.enabledEditControl)
    );
  }
  get airlineCode(): string {
    return this._airlineCode;
  }
  get params$(): Observable<Params> {
    return this.route.params;
  }

  get departurePlace$(): Observable<AutocompletePlace> {
    if (this.searchType === SearchType.BUSES_RESULTS || this.searchType === SearchType.HOME_BUSES) {
      return this.facade.departureTerminal$;
    } else return this.facade.departurePlace$;
  }

  get arrivalPlace$(): Observable<AutocompletePlace> {
    if (this.searchType === SearchType.BUSES_RESULTS || this.searchType === SearchType.HOME_BUSES) {
      return this.facade.arrivalTerminal$;
    } else return this.facade.arrivalPlace$;
  }

  get departureDate$(): Observable<Date> {
    return this.facade.departureDate$;
  }
  get arrivalDate$(): Observable<Date> {
    return this.facade.arrivalDate$;
  }
  get flightType$(): Observable<FlightType> {
    return this.facade.flightType$;
  }
  get flightClass$(): Observable<FlightClass> {
    return this.facade.flightClass$;
  }
  get isLoadingAutocompletePlaces$(): Observable<boolean> {
    return this.facade.isLoadingAutocompletePlaces$;
  }
  get baggageIncluded$(): Observable<boolean> {
    return this.facade.baggageIncluded$;
  }
  get passengerArrangement$(): Observable<PassengerArrangement[]> {
    return this.facade.passengerArrangements$;
  }
  get autoCompletePlaces$(): Observable<AutocompletePlaces> {
    return this.facade.autoCompletePlaces$;
  }
  get isOneWayTrip$(): Observable<boolean> {
    return this.flightType$.pipe(map(flightType => flightType === FlightType.ONEWAY));
  }

  get isResultPage(): boolean {
    return this.searchType === SearchType.FLIGHT_RESULTS ||
      this.searchType === SearchType.BUSES_RESULTS || this.exchangeSearch;
  }

  get isBuses(): boolean {
    return this.searchType === SearchType.BUSES_RESULTS || this.searchType === SearchType.HOME_BUSES;
  }
  get exchangeSearch(): boolean {
    return this.searchType === SearchType.FLIGHT_USER_HISTORY || this.isExchangeSearch;
  }

  get editSearchResults(): boolean {
    return this.searchType === SearchType.FLIGHT_RESULTS ||
      this.searchType === SearchType.BUSES_RESULTS || this.exchangeSearch;
  }

  get exchangeData(): ExchangeSearchParamsSectors {
    return this._exchangeData;
  }
  get exchangeTripType(): TripTypes {
    return exchangeTripTypeMapper(this.exchangeData);
  }

  get departureDateIsDisabled(): boolean {
    return this.exchangeSearch ? !!this.exchangeData &&
      this.exchangeData.departureSelectionDisabled : false;
  }

  get isAlert(): boolean {
    return this.searchType === SearchType.ALERT;
  }

  private get searchByEvent(): SearchBy {
    return this.isBuses ? SearchBy.BUSES : SearchBy.FLIGHTS;
  }

  public setPassengerArrangement(event: PassengerArrangement): void {
    this.facade.setPassengerArrangement(event);
  }

  public selectFlightType(event: FlightType | BusType): void {
    this.facade.selectFlightType(event.toString() as FlightType);
  }

  public setFlightClass(event: FlightClass): void {
    this.facade.selectFlightClass(event);
  }

  public selectPlace(place: SelectAutocompletePlace): void {
    this.facade.selectPlace(place, false, this.searchType);
    this.facade.searchData$.subscribe(searchData => {
      const search = { ...searchData };
      if (this.searchType === SearchType.BUSES_RESULTS || this.searchType === SearchType.HOME_BUSES) {
        if (place.type === PlaceType.DEPARTURE) search.departureTerminal = place; else search.arrivalTerminal = place;
      } else {
        if (place.type === PlaceType.DEPARTURE) search.departurePlace = place; else search.arrivalPlace = place;
      }
      this.facade.saveSearchData(search, (this.isBuses ? SearchBy.BUSES : SearchBy.FLIGHTS));
    }).unsubscribe();
  }

  public selectCalendar(calendar: Date[]): void {
    if (!isEmpty(calendar)) this.facade.selectCalendarDates(calendar);
  }

  public selectDateCalendar(calendar: Date[]): void {
    this.facade.selectCalendarDates(calendar);
  }

  public toggleSearchModule(search: SearchData = null): void {
    zip(
      this.isEditButtonVisible$, this.editSearchIsActivated$,
    ).pipe(
      take(1),
      tap(([isEditButtonVisible, editSearchIsActivated]) => {
        if (this.exchangeSearch || (!this.exchangeSearch && isEditButtonVisible)) {
          const current = editSearchIsActivated ? this.headerProductType : null;
          this.facade.setEditSearchByProduct(current, this.headerProductType);
        }
      }),
    ).subscribe().unsubscribe();
  }

  private registerViewTravelSearchScreenByEdit(): Subscription {
    return combineLatest([
      this.editSearchIsActivated$, this.isEditButtonVisible$
    ]).pipe(
      switchMap(([editSearchIsActivated, isEditButtonVisible]) => iif(
        () => (
          this.exchangeSearch || (!this.exchangeSearch && !!isEditButtonVisible)) && (
          editSearchIsActivated &&
          (this.isResultPage || this.currentUrl.indexOf(`/${homeItemDetailRootRoute}/`) >= 0)
        ),
        this.avoService.getEventTriggerProps$(
          AVO_EVENTS.VIEW_TRAVEL_SEARCH_SCREEN, {
            SOURCE: this.source, TRAVEL_PRODUCT_TYPE: this.searchByEvent
          }).pipe(
          tap(triggerView => {
            this.AvoDispatch.viewTravelSearchScreen({
              ...this.avoService.avoBaseProperties(triggerView),
              source: this.avoService.getAvoSourceValueType(this.source),
              travelProductType: this.searchByEvent
            });
          })
        ),
        of(null)
      ))
    ).subscribe();
  }

  public swapPlaces(): void {
    this.facade.swapPlaces(this.searchType);
  }

  public placeInputChanged(input: ChangeInputPayload): void {
    if (input.isEmpty) this.facade.resetAutocomplete();
    else this.facade.searchPlaces(input.value);
  }

  public includeBaggage(included: boolean): void {
    this.facade.includeBaggage(included);
  }
  
  public search(): void {
    this.refreshSearchFacade.setRefreshTimer(true);
    const searchProperties$ = this.isBuses
      ? this.amplitude.searchProperties$(SearchBy.BUSES)
      : this.amplitude.searchProperties$(SearchBy.FLIGHTS);
  
    zip(this.facade.searchData$.pipe(take(1)), searchProperties$.pipe(take(1)))
      .pipe(
        map(([data, searchProps]) => {
          const urlParams = mapSearchData(data, this.isBuses);
          return {
            data,
            urlParams,
            isCurrentUrl: this.currentUrl.includes(urlParams),
            searchProps
          }
        }),
        tap(({ isCurrentUrl }) => {
          if ((isCurrentUrl || this.isCheckedFlexDate) && this.isResultPage) {
            this.toggleMobileSearch();
          }
        }),
        first(({ isCurrentUrl }) => !isCurrentUrl || (isCurrentUrl && this.isCheckedFlexDate)),
        tap(({ data, urlParams }) => {
          this.handleSearchType(data, urlParams);
        }),
        switchMap(({ searchProps }) => this.getEventTriggerProps$(searchProps)),
        tap(trigger => {
          this.isCheckedFlexDate
            ? this.AvoDispatch.selectFlexdates(this.buildAvoFlexdatesParams(trigger))
            : this.AvoDispatch.selectTravelSearch(this.buildAvoTravelSearchParams(trigger));
        })
      )
      .subscribe()
      .unsubscribe();
  }

  private goSearchResults(data: SearchData, urlParams:string): void {
    this.router.navigate([`${flightResultsRootRoute}/${urlParams}`], {
      queryParams: { 
        source: this.source, 
        branch_code: data.branchCode,
        departure_type: this.typeSearchFlight(data.departurePlace),
        arrival_type: this.typeSearchFlight(data.arrivalPlace),
        [SALES_CHANNEL]: data.salesChannel 
      }
    });
    this.facade.saveSearchData(data, (this.isBuses ? SearchBy.BUSES : SearchBy.FLIGHTS)); this.toggleMobileSearch();
  }

  public showMobileCalendar(type: CalendarField): void {
    this.showMobileCalendarEvent.emit(type);
  }
  public clearAutocompletePlaces(): void {
    this.facade.resetAutocomplete();
  }

  public filterPassengerArrangement(passengerArrangement: PassengerArrangement[]): PassengerArrangement[] {
    if (this.searchType === SearchType.BUSES_RESULTS || this.searchType === SearchType.HOME_BUSES) {
      return passengerArrangement.filter(x => x.id === PassengerType.ADULTS);
    }
    return passengerArrangement;
  }

  public amplitudeEvent(params: AmplitudeEventParams): void {
    this.avoService.getEventTriggerProps$(params.type, params.props)
      .pipe(tap(trigger => {
        switch (params.type) {
          case AVO_EVENTS.TRAVEL_LOCATION_NOT_FOUND:
            this.AvoDispatch.travelLocationNotFound({
              ...this.avoService.avoBaseProperties(trigger),
              searchQuery: params.props.SEARCH_QUERY as string,
              controlId: params.props.CONTROL_ID as string,
              travelProductType: params.props.TRAVEL_PRODUCT_TYPE as string,
            })
            break;
          case AVO_EVENTS.SELECT_CHANGES_SEARCH:
            this.AvoDispatch.selectChangesSearch({
              ...this.avoService.avoBaseProperties(trigger),
              allSegments: params.props.ALL_SEGMENTS as boolean,
            });
            break;
          default:
            triggerAmplitudeEventApp(trigger);
            break;
        }
      })).subscribe().unsubscribe();
  }

  public toggleSearchByFlexDates(isChecked: boolean): void {
    this.isCheckedFlexDate = isChecked;
  }

  private typeSearchFlight(place: AutocompletePlace): string {
    return !isNullOrUndefined(place.is_airport) && place.is_airport ? 
      SearchDataDefaultType.AIRPORT.toLowerCase():
      SearchDataDefaultType.CITY.toLowerCase();
  }

  public toggleMobileSearch(): void {
    if (this.isMobile) {
      this.toggleSearchModule();
      this.viewportScroller.scrollToPosition([0, 0]);
    }
  }

  private goExchangeSearch(data: SearchData): void {
    this.exchangeData = {
      ...this.exchangeData,
      exchangeWithAssistanceAndLongerDuration: getExchangeWithAssistanceAndLongerDuration(
        this.exchangeData.withTravelAssistance, this.exchangeData.originalDeparture,
        this.exchangeData.originalArrival, data
      ),
      exchangeWithAssistanceAndShorterOrEqualDuration: getExchangeWithAssistanceAndShorterOrEqualDuration(
        this.exchangeData.withTravelAssistance, this.exchangeData.originalDeparture,
        this.exchangeData.originalArrival, data
      ),
    };
    const exchangeParams = mapExchangeSearchData({ ...this.exchangeData, baggageIncluded: data.baggageIncluded }, data);
    this.currentUrl = `${flightExchangeRootRoute}/${exchangeParams}`;
    this.router.navigate([`${flightExchangeRootRoute}/${exchangeParams}`], {
      queryParams: {
        source: this.source, branch_code: data.branchCode,
        sector_disabled: JSON.stringify(this.exchangeData.exchangeDisabledSector),
        departure_type: this.typeSearchFlight(data.departurePlace),
        arrival_type: this.typeSearchFlight(data.arrivalPlace),
      }
    });
    this.facade.saveExchangeSearchData(data); 
    this.toggleMobileSearch();
  }

  private goFlexDates(data: SearchData, urlParams:string): void {
    const flexDatesPayload = {
      ...mapFlexDatesPayload({
        ...mapUrlFlexDatesParams(urlParams), 
        departure_type: this.typeSearchFlight(data.departurePlace),
        arrival_type: this.typeSearchFlight(data.arrivalPlace),
      },
      true),
      RequestSettings: getBaseRequestSettings(this.isMobile)
    }
    this.facade.setSkipServices(true);
    this.flightResultFacade.searchflexDates(flexDatesPayload);
    this.router.navigate([`${flightResultsRootRoute}/${urlParams}`, { outlets: { 'detail': [flexiDatesRoute] } }], {
      queryParams: { 
        branch_code: data.branchCode,
        [SALES_CHANNEL]: data.salesChannel,
        source: this.source,
        departure_type: this.typeSearchFlight(data.departurePlace),
        arrival_type: this.typeSearchFlight(data.arrivalPlace),
      },
      skipLocationChange: true
    });
    this.facade.saveSearchData(data, SearchBy.FLIGHTS);
  }

  private handleSearchType(data: SearchData, urlParams: string): void {
    switch (this.searchType) {
      case SearchType.HOME_BUSES:
      case SearchType.BUSES_RESULTS:
        this.handleBusesSearch(data, urlParams);
        break;
      default:
        this.handleDefaultSearch(data, urlParams);
        break;
    }
  }
  
  private handleBusesSearch(data: SearchData, urlParams: string): void {
    this.currentUrl = `${busResultsRootRoute}/${urlParams}`;
    this.router.navigate([`${busResultsRootRoute}/${urlParams}`], {
      queryParams: { source: this.source, branch_code: data.branchCode, [SALES_CHANNEL]: data.salesChannel }
    });
    this.facade.saveSearchData(data, this.isBuses ? SearchBy.BUSES : SearchBy.FLIGHTS);
    this.toggleMobileSearch();
  }
  
  private handleDefaultSearch(data: SearchData, urlParams: string): void {
    this.appFacade.resetWidgetData();
  
    if (this.exchangeSearch) {
      this.goExchangeSearch(data);
    } else {
      const blockByDates = !canSearch(RappiTravelModules.flighs) && blockSearchByDates(data.arrivalPlace, data.departureDate);
      const blockByCountry = blockSearchByCountry(data.arrivalPlace, data.departurePlace, data.departureDate);
  
      if (blockByDates || blockByCountry) {
        this.handleBlockedSearch(data);
      } else {
        this.handleSearchResults(data, urlParams);
      }
    }
  }
  
  private handleBlockedSearch(data: SearchData): void {
    const messageType = isNationalSearch(data.arrivalPlace) ? 'FLIGHT_NATIONAL' : 'FLIGHT_INTERNATIONAL';
    const untilDate = untilDateByCountry(data.arrivalPlace, data.departurePlace);
    const untilDateByType = isNationalSearch(data.arrivalPlace) 
      ? untilDate.national_dates.maxDate : 
      untilDate.international_dates.maxDate;
  
    const formattedDate = formatDate(
      new Date(untilDateByType.replace(/-/g, '/')),
      this.datePipe,
      this.translatePipe
    );
  
    this.appFacade.fetchToastrMessage({
      type: ToastrTypes.ERROR,
      message: this.translateService.translate(`BLOCK_SEARCH_MESSAGE.${messageType}`, { untilDate: formattedDate }),
    });
  }
  
  private handleSearchResults(data: SearchData, urlParams: string): void {
    this.currentUrl = `${flightResultsRootRoute}/${urlParams}`;
  
    if (this.isCheckedFlexDate) {
      this.goFlexDates(data, urlParams);
    } else {
      this.goSearchResults(data, urlParams);
    }
  }
  
  private getEventTriggerProps$(searchProps: SearchProperties): Observable<TriggerEvent> {
    const eventType = this.isCheckedFlexDate ? AVO_EVENTS.SELECT_FLEXDATES : AVO_EVENTS.SELECT_TRAVEL_SEARCH;
    const eventProperties = { SOURCE: this.source };
  
    return this.avoService.getEventTriggerProps$(eventType, eventProperties, searchProps);
  }
  
  private buildAvoFlexdatesParams(trigger: TriggerEvent): SelectFlexdatesProperties {
    return {
      ...this.avoService.avoBaseProperties(trigger),
      ...this.avoService.avoSearchProps(trigger?.SEARCH_PROPS),
    };
  }
  
  private buildAvoTravelSearchParams(trigger: TriggerEvent): SelectTravelSearchProperties {
    return {
      ...this.avoService.avoBaseProperties(trigger),
      source: this.avoService.getAvoSourceValueType(this.source),
    };
  }

  private fetchDefaultPlace(): void {
    if (isNullOrUndefined(this.defaultPlace) || isNullOrUndefined(this.defaultPlace.code)) {
      if (this.searchType === SearchType.HOME_BUSES || this.searchType === SearchType.BUSES_RESULTS) {
        this.facade.isSearchTerminalForIp$.subscribe(isSearch => {
          if (!isSearch) this.facade.fetchDefaultPlace(this.searchType);
        }).unsubscribe();
      } else if (this.searchType === SearchType.HOME_FLIGHT || this.searchType === SearchType.FLIGHT_RESULTS) {
        this.facade.isSearchAirportForIp$.subscribe(isSearch => {
          if (!isSearch) this.facade.fetchDefaultPlace(this.searchType);
        }).unsubscribe();
      }
    }
  }

  private setDefaultPlace(): void {
    this.subscriptions.push(this.facade.defaultAirport$.subscribe(place => {
      this.defaultPlace = this.searchType === SearchType.BUSES_RESULTS || this.searchType === SearchType.HOME_BUSES ?
        this.defaultPlace : place; this.setDefaultDeparturePlace();
    }));
  }

  private setDefaultDeparturePlace(): void {
    this.subscriptions.push(combineLatest([this.facade.departurePlace$, this.arrivalPlace$]).subscribe(data => {
      const departurePlace = data[0];
      const arrivalPlace = data[1];
      if (isNullOrUndefined(departurePlace?.code) && isNullOrUndefined(arrivalPlace?.code) &&
        !isNullOrUndefined(this.defaultPlace?.code)) {
        this.facade.selectPlace(this.defaultPlace);
      }
    }));
  }

  private registerViewSearchAmplitudeEvent(): void {
    this.editSearchIsActivated$.pipe(
      tap(editSearchIsActivated => {
        if (editSearchIsActivated || this.showHeaderMobile) {
          this.amplitudeEvent({
            type: AVO_EVENTS.VIEW_TRAVEL_SEARCH_SCREEN,
            props: { SOURCE: this.source, TRAVEL_PRODUCT_TYPE: this.isBuses ? SearchBy.BUSES : SearchBy.FLIGHTS }
          });
        }
      })
    ).subscribe().unsubscribe();
  }
}
